/* Build your rig (Desk t3886), round 5: Spec sheet + Tiles. The engine, the spec-sheet stage, the tiles stage and the
   view switch from data/design/rig-builder-t3886/round-5/src (engine.js, a.js, c.js, switch.js), joined in one file and
   scoped to #rig-root so it can live on a theme page.

   Two sources, joined by product handle:
     assets/rig-builder-config.json            the fit rules: which parts fit which camera, slot, weight, Wh (one file).
     /products/<handle>?view=rig-data          live store data per part: price, availability, stock, policy, variants, photo
                                               (templates/product.rig-data.liquid).
   Prices and stock are never kept in the config, so they cannot go stale. To read the fit rules from metaobjects
   later, change load() only; the engine below takes {config, live} and does not care where they came from.
   Stock is never shown per part and never blocks a pick (Travis, 2026-10-26). At the end of the flow the picked parts are
   grouped as In store / Ready to ship / Needs to be ordered (where() below), and Add to cart adds what Shopify will take. */
(function () {
  'use strict';
  const root = document.getElementById('rig-root');
  if (!root) return;
  const ROUTES = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const esc = s => String(s).replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]));
  const $ = id => document.getElementById('rig-' + id);

  /* ---------- load: the config, then every part's live data (a few at a time, one retry on 429 / 5xx) ---------- */
  async function getJSON(url) {
    for (let tryN = 0; ; tryN++) {
      const r = await fetch(url); // no Accept header: with one, Shopify answers a product URL with its own product JSON, not our template
      if (r.ok) return JSON.parse(await r.text());
      if (tryN === 0 && (r.status === 429 || r.status >= 500)) { await new Promise(done => setTimeout(done, 600)); continue; }
      throw new Error(url + ' ' + r.status);
    }
  }
  /* Two steps, so 12 cameras load as fast as one did (t3886): first every camera body, the example rig's parts, and a shared
     link's camera with its parts; the page starts on those. The other cameras' parts follow in the background
     (loaded.rest) and join the page when they arrive; a camera picked before then says its parts are loading. */
  async function load() {
    const config = await getJSON(root.dataset.config);
    const live = {}, tried = new Set();
    const fetchAll = async hs => {
      const queue = hs.slice();
      const worker = async () => {
        while (queue.length) {
          const h = queue.shift();
          try { live[h] = await getJSON(root.dataset.products + h + '?view=' + root.dataset.productView); } catch (e) { /* not on the storefront: left out */ }
          tried.add(h);
        }
      };
      await Promise.all(Array.from({length: 8}, worker));
    };
    const handles = Object.keys(config.parts), idTo = Object.fromEntries(handles.map(h => [config.parts[h].id, h]));
    const shared = (new URLSearchParams(location.hash.slice(1)).get('r') || '').split(',').map(i => idTo[i]).filter(Boolean);
    const famOf = h => config.cameras.find(c => c.status === 'ready' && c.bodies.includes(h));
    const example = ((config.example && config.example.ids) || []).map(i => idTo[i]).filter(Boolean);
    const first = new Set([...handles.filter(h => config.parts[h].slot === 'camera'), ...example, ...shared, ...shared.map(famOf).filter(Boolean).flatMap(f => f.parts)]);
    await fetchAll([...first]);
    const rest = handles.filter(h => !first.has(h));
    return {config, live, tried, rest: rest.length ? fetchAll(rest) : null};
  }
  function showLoading() {
    $('list').innerHTML = '<p class="more">Loading the parts…</p>';
    $('quick').innerHTML = '';
  }
  function showError() {
    $('list').innerHTML = '<p class="more">We could not load the parts just now. <button type="button" class="clear" onclick="location.reload()">Try again</button></p>';
  }
  showLoading();
  load().then(start).catch(e => { if (window.console) console.error('Build your rig', e); showError(); });

  function start(loaded) {
    const CFG = loaded.config;

    /* ---------- the sheet layer: the sheet, its backdrop and the toast, moved to the end of <body> (see rig-builder.css) ---------- */
    const layer = $('layer') || (() => {   // a theme copy of the section from before the layer existed: build it here
      const l = document.createElement('div'); l.className = 'rigb rigb-layer'; l.id = 'rig-layer';
      const sh = $('sheet'), t = $('sheetT');
      ['sheetback', 'sheet', 'toast'].forEach(k => l.appendChild($(k)));
      sh.insertAdjacentHTML('afterbegin', '<div class="grab" aria-hidden="true"><i></i></div>');
      const box = document.createElement('div'); box.className = 'sht'; t.parentNode.insertBefore(box, t); box.appendChild(t);
      box.insertAdjacentHTML('beforeend', '<p class="sheetsub" id="rig-sheetS"></p>');
      return l;
    })();
    document.body.appendChild(layer);
    const SHEET = $('sheet'), BACK = $('sheetback'), BODY = $('sheetBody');
    let closeT = 0;
    // fit the layer to what the visitor can see: on iPhone Safari the toolbars come and go and the layout viewport is not
    // the visible one. --rig-obst = whatever is pinned over the bottom edge above every page element (Shopify's preview bar
    // on a theme preview; nothing for customers), measured, never assumed.
    const VV = window.visualViewport;
    const COVERS = '#PBarNextFrameWrapper,#preview-bar-iframe';
    function bottomCover() {
      const bottom = VV ? VV.offsetTop + VV.height : innerHeight; let h = 0;
      document.querySelectorAll(COVERS).forEach(el => { const r = el.getBoundingClientRect(); if (r.height > 0 && r.width >= innerWidth * 0.6 && r.top < bottom && r.bottom >= bottom - 4) h = Math.max(h, bottom - r.top); });
      return Math.round(Math.min(h, 140));
    }
    function fitLayer() {
      if (VV) { layer.style.setProperty('--rig-vvh', VV.height + 'px'); layer.style.setProperty('--rig-vvt', VV.offsetTop + 'px'); }
      document.documentElement.style.setProperty('--rig-obst', bottomCover() + 'px');
    }
    let fitR = 0; const fitSoon = () => { cancelAnimationFrame(fitR); fitR = requestAnimationFrame(fitLayer); };
    if (VV) { VV.addEventListener('resize', fitSoon); VV.addEventListener('scroll', fitSoon); }
    window.addEventListener('resize', fitSoon);
    fitLayer(); [800, 2500, 6000].forEach(ms => setTimeout(fitLayer, ms));   // the preview bar is injected after load

    /* ---------- parts: fit rules (config) + live data (products), joined by handle ---------- */
    const FAMS = CFG.cameras.filter(f => f.status === 'ready');
    const FAM = {};   // family id -> Set of part handles checked to fit that camera
    FAMS.forEach(f => { FAM[f.id] = new Set(f.parts); });
    const famOfBody = handle => { const f = FAMS.find(x => x.bodies.includes(handle)); return f ? f.id : null; };
    // cut-outs sit next to the config file in assets/ and share its ?v= so a new config busts their cache too
    const cutoutUrl = file => { const u = root.dataset.config.split('?'); return u[0].replace(/[^/]*$/, '') + file + (u[1] ? '?' + u[1] : ''); };
    const P = [], BY = {}, ORDER = Object.keys(CFG.parts);
    const missing = [];
    // joins the live data that has arrived to the config; called once at the start and again when the rest arrives
    const addParts = () => ORDER.forEach(handle => {
      if (!loaded.tried.has(handle) || P.some(x => x.handle === handle) || missing.includes(handle)) return;
      const m = CFG.parts[handle], pr = loaded.live[handle];
      const v = pr && (pr.variants.find(x => x.id === m.variant) || pr.variants[0]);
      if (!v) { missing.push(handle); return; }
      BY[m.id] = P[P.push({
        id: m.id, handle, slot: m.slot, name: pr.title, short: m.short || pr.title, vendor: pr.vendor, url: pr.url,
        price: v.price / 100, avail: v.available, managed: v.managed, stock: v.managed ? v.qty : null, policy: v.policy,
        inStore: v.in_store === true, supplier: pr.supplier === true,
        variantId: v.id, variantTitle: pr.variants.length > 1 ? v.title : '',
        img: m.cutout ? cutoutUrl(m.cutout) : null, photo: pr.photo, ph: !!m.ph, exact: m.exact || false, w: m.w || 0, wUnknown: !!m.wUnknown, watt: m.watt, wh: m.wh, dtaps: m.dtaps, feeds: !!m.feeds,
        needsXlr: m.needsXlr, addsXlr: !!m.addsXlr, hawk: m.hawk, incl: m.incl, note: m.note,
        fam: m.slot === 'camera' ? famOfBody(handle) : null, label: m.label, xlr: m.xlr, xlrHint: m.xlrHint, lens: m.lens, ar: m.ar
      }) - 1];
    });
    const sortParts = () => P.sort((a, b) => ORDER.indexOf(a.handle) - ORDER.indexOf(b.handle));
    addParts(); sortParts();
    if (!P.length) { showError(); return; }
    // the parts of the other cameras are still on their way: which cameras have everything already
    let pending = !!loaded.rest;
    // (a part counts once it has joined the page, not when its request returns: the rest join together when the last one is back)
    const famPending = () => { const c = one('camera'); return pending && !!c && !!FAM[c.fam] && [...FAM[c.fam]].some(h => !BY[CFG.parts[h].id] && !missing.includes(h)); };
    if (loaded.rest) loaded.rest.then(() => {
      addParts(); sortParts(); pending = false;
      if (missing.length && window.console) console.warn('Build your rig: not on the storefront, left out:', missing);
      render();
    });
    else if (missing.length && window.console) console.warn('Build your rig: not on the storefront, left out:', missing);
    const COMING = CFG.cameras.filter(f => f.status !== 'ready' && f.listed !== false).map(f => f.name);
    const FAMNOTE = Object.fromEntries(FAMS.filter(f => f.note).map(f => [f.id, f.note]));

    const SLOT = {camera: {t: 'Camera body', n: 1, s: 'Camera', add: 'Pick a camera'}, cage: {t: 'Cage', n: 2, s: 'Cage', add: 'Add a cage'}, rail: {t: 'NATO rail and clamps', n: 3, s: 'NATO rail', multi: true, add: 'Add a NATO rail'},
      handle: {t: 'Top handle', n: 4, s: 'Handle', add: 'Add a handle'}, onmic: {t: 'On-camera microphone', n: 5, s: 'Mic', add: 'Add a mic'}, wireless: {t: 'Wireless or clip-on mic', n: 6, s: 'Wireless', add: 'Add wireless'},
      arm: {t: 'Articulating arm', n: 7, s: 'Arm', add: 'Add an arm'}, phone: {t: 'Phone mount', n: 8, s: 'Phone mount', add: 'Add a phone mount'}, monitor: {t: 'Monitor', n: 9, s: 'Monitor', add: 'Add a monitor'},
      mmount: {t: 'Monitor mount', n: 10, s: 'Monitor mount', add: 'Add a mount'}, plate: {t: 'V-mount plate', n: 11, s: 'V-plate', add: 'Add a V-plate'}, battery: {t: 'V-mount battery', n: 12, s: 'Battery', add: 'Add a battery'},
      cable: {t: 'D-Tap dummy cable', n: 13, s: 'D-Tap cable', add: 'Add the cable'}, card: {t: 'Memory card', n: 14, s: 'Card', add: 'Add a card'}, ff: {t: 'Follow focus', n: 15, s: 'Follow focus', add: 'Add follow focus'}};
    const TABS = [
      {id: 'camera', label: 'Camera', slots: ['camera'], hint: 'Start here. Parts that are not listed for your body are hidden.'},
      {id: 'cage', label: 'Cage', slots: ['cage'], hint: 'The cage is what everything else mounts to.'},
      {id: 'rail', label: 'NATO rail', slots: ['rail'], hint: 'A cage already has a top rail. These add another NATO position.'},
      {id: 'arm', label: 'Arm + phone', slots: ['arm', 'phone'], hint: 'An articulating arm with a phone mount, or anything else on a ball head.'},
      {id: 'handle', label: 'Handle', slots: ['handle'], hint: 'Sits on the top NATO rail.'},
      {id: 'audio', label: 'Audio', slots: ['onmic', 'wireless'], hint: 'One on-camera mic and one wireless set at most.'},
      {id: 'monitor', label: 'Monitor', slots: ['monitor', 'mmount'], hint: 'A monitor needs a mount.'},
      {id: 'power', label: 'Power', slots: ['plate', 'battery', 'cable'], hint: 'Plate, battery and the dummy cable work as a chain.'},
      {id: 'media', label: 'Extras', slots: ['card', 'ff'], hint: 'Memory card, and a follow focus on the front rods.'}
    ];
    const SLOTS = Object.keys(SLOT);
    const NEXT_ORDER = ['camera', 'cage', 'handle', 'onmic', 'monitor', 'mmount', 'arm', 'phone', 'plate', 'battery', 'cable', 'card', 'wireless', 'ff', 'rail'];
    const EXAMPLE = CFG.example || null;

    /* ---------- state + helpers ---------- */
    const S = {tab: 'camera', sel: {}, pin: null, undo: null, arrived: null, view: 'spec', sheet: null};
    const RM = matchMedia('(prefers-reduced-motion: reduce)');
    const DESK = matchMedia('(min-width:1000px)');
    const money = n => '$' + n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    const items = slot => (S.sel[slot] || []).map(id => BY[id]);
    const one = slot => items(slot)[0] || null;
    const allPicked = () => Object.values(S.sel).flat().map(id => BY[id]);
    const pad = n => String(n).padStart(2, '0');
    // the cut-out if there is one, else the store photo on a light plate; none for a part whose photo is not a clean product shot
    const hasImg = p => !!p && !p.ph && !!(p.img || p.photo);
    const imgHTML = (p, alt, lazy) => `<img${lazy ? ' loading="lazy"' : ''} src="${p.img || p.photo}" alt="${esc(alt || '')}"${p.img ? '' : ' class="photo"'}>`;
    /* Where a part is, for the summary at the end (never drawn per part, never blocks a pick):
         order = Shopify will not sell it (Continue selling off, 0 on hand) or nothing is on hand anywhere and it would be a
                 backorder: we order it in for the customer
         store = the Oak Creek store has it (variant metafield custom.in_store_available, kept by the badge sync)
         ship  = not in the store but on hand where we ship from, or the supplier has it (product metafield custom.supplier_available)
       Continue selling ON alone is NOT "ready to ship": with nothing on hand or at the supplier it is a backorder
       (storefront-availability-badge playbook), so it says "needs to be ordered". */
    function where(p) {
      if (!p.avail) return 'order';
      if (p.inStore) return 'store';
      if ((p.managed && p.stock > 0) || p.supplier) return 'ship';
      return 'order';
    }
    const WHERE = [
      {k: 'store', t: 'In store', s: 'On the shelf at Oak Creek now.'},
      {k: 'ship', t: 'Ready to ship', s: 'Not on our shelf, but we can ship it to you.'},
      {k: 'order', t: 'Needs to be ordered', s: 'We order these in for you. They ship when they arrive.'}
    ];
    const groupsOf = ps => WHERE.map(w => ({...w, ps: ps.filter(p => where(p) === w.k)}));
    const fitsList = p => { const c = one('camera'); return !c || p.slot === 'camera' || !FAM[c.fam] || FAM[c.fam].has(p.handle); };
    const compat = fitsList;
    const fitsExact = p => { const c = one('camera'); return !!(p.exact && c && FAM[c.fam] && FAM[c.fam].has(p.handle) && (p.exact === true || (Array.isArray(p.exact) && p.exact.includes(c.fam)))); };
    // a slot is offered for the picked camera only if at least one part in it is listed for that camera (no dead-end "+")
    const offered = slot => slot === 'camera' || famPending() || P.some(p => p.slot === slot && compat(p));
    // no cage listed for this camera: mics and wireless sit on the camera's own shoe, and a shoe-mounted handle unit (addsXlr) too
    const noCage = () => !!one('camera') && !offered('cage');
    const hasXlrHandle = () => items('handle').some(p => p.addsXlr);
    const kit = () => { const g = one('cage'); return g && g.incl ? g.incl : []; };
    const hasHandle = () => !!one('handle') || kit().includes('handle');
    const hasPlate = () => !!one('plate') || kit().includes('plate');
    const feedsCam = () => !!one('plate') && one('plate').feeds;
    // the dummy-cable slot is not missing anything when the picked plate already runs the camera
    const notNeeded = slot => slot === 'cable' && feedsCam() && !items('cable').length;
    const snapshot = () => JSON.stringify(S.sel);
    const tabOf = slot => TABS.find(t => t.slots.includes(slot));
    const slotName = slot => { const it = items(slot); return it.length > 1 ? 'NATO rail + clamp' : it.length ? it[0].short : ''; };
    const slotPrice = slot => items(slot).reduce((s, p) => s + p.price, 0);
    function setSel(slot, ids) { if (ids.length) S.sel[slot] = ids; else delete S.sel[slot]; }
    function hostMissing(slot) {
      const shoe = () => !!one('cage') || noCage();
      const H = {handle: () => !!one('cage') || (noCage() && P.some(p => p.slot === 'handle' && p.addsXlr && compat(p))), onmic: shoe, wireless: shoe, rail: () => !!one('cage'), arm: () => !!one('cage'), mmount: () => !!one('cage'), plate: () => !!one('cage'), ff: () => !!one('cage'),
        cage: () => !!one('camera'), battery: () => hasPlate(), phone: () => !!one('arm'), monitor: () => !!one('mmount'), cable: () => hasPlate(), card: () => !!one('camera')};
      return H[slot] ? !H[slot]() : false;
    }

    function pick(id) {
      const p = BY[id];
      const before = calc().total, prev = snapshot();
      const cur = S.sel[p.slot] || [], has = cur.includes(id), multi = SLOT[p.slot].multi;
      const old = !multi && cur.length && !has ? BY[cur[0]] : null;
      setSel(p.slot, multi ? (has ? cur.filter(x => x !== id) : [...cur, id]) : (has ? [] : [id]));
      const dropped = [];
      if (p.slot === 'camera' && !has) {
        SLOTS.forEach(sl => { const keep = (S.sel[sl] || []).filter(i => { const ok = compat(BY[i]); if (!ok) dropped.push(BY[i].short); return ok; }); setSel(sl, keep); });
      }
      // on a phone the choices are a sheet: a single-choice slot is done once picked; a "pick any" slot stays open for the next one
      const done = !!(S.sheet && S.sheet.kind === 'pick' && !multi);
      S.undo = prev; S.pin = p.slot; S.arrived = has || done ? null : p.slot; S.just = has ? null : id;
      showDelta(calc().total - before);
      const say = () => {
        if (has) toastMsg('Removed ' + esc(p.short) + '.', true);
        else if (dropped.length) toastMsg('Removed ' + dropped.length + ' part' + (dropped.length > 1 ? 's' : '') + ' not listed for the ' + p.label + ': ' + esc(dropped.join(', ')) + '.', true);
        else if (old) toastMsg('Swapped ' + esc(old.short) + ' for ' + esc(p.short) + '.', true);
        else hideToast();
      };
      announce((has ? 'Removed ' : 'Added ') + p.short + '.');
      if (!done) say(); else hideToast();
      render();
      S.arrived = null; S.just = null;
      if (!done) return;
      // the sheet shows the tick land for a moment, slides away, then the part arrives on the rig, in view (and the toast,
      // if there is one, appears where it belongs: above the rig bar, not over the sheet)
      S.sheet.closing = true;
      const sheetNow = S.sheet;
      setTimeout(() => { if (S.sheet === sheetNow) { closeSheet(true); arrive(p.slot, !has); } say(); }, RM.matches ? 0 : 160);
    }
    // after a pick from the sheet: light the slot's row on the rig, give it focus and scroll it into view if it is off screen
    function arrive(slot, lit) {
      if (lit && !RM.matches) { S.arrived = slot; Stage.render(); S.arrived = null; }
      focusSlot(slot);
      revealSlot(slot);
    }
    function revealSlot(slot) {
      if (DESK.matches) return;
      const el = slotEl(slot); if (!el) return;
      const r = el.getBoundingClientRect(), top = topCover() + 8;
      const bar = root.querySelector('.bar').getBoundingClientRect(), bottom = Math.min(innerHeight, bar.top) - 8;
      if (r.top >= top && r.bottom <= bottom) return;
      const room = bottom - top, y = r.height < room ? r.top - top - (room - r.height) / 2 : r.top - top;
      window.scrollBy({top: y, behavior: RM.matches ? 'auto' : 'smooth'});
    }
    // how much of the top of the screen the theme's pinned header covers (0 if it is not pinned there)
    function topCover() {
      let h = parseFloat(getComputedStyle(root).getPropertyValue('--rig-sticky')) || 0;
      for (const el of document.elementsFromPoint(innerWidth / 2, 2)) {
        if (layer.contains(el) || root.contains(el)) continue;
        for (let a = el; a && a !== document.body && a !== document.documentElement; a = a.parentElement) {
          const pos = getComputedStyle(a).position;
          if (pos === 'fixed' || pos === 'sticky') { const r = a.getBoundingClientRect(); if (r.top <= 2 && r.height < innerHeight / 2) h = Math.max(h, r.bottom); break; }
        }
      }
      return h;
    }
    /* Tapping a slot (a "+" , a filled row, an empty pocket, "Next") opens the choices for exactly that slot:
       computer = the All parts panel jumps to that slot and lights it up; phone = the same choices open as a sheet. */
    function openSlot(slot, opener) {
      S.pin = slot; StageA.zone = null;   // the spec sheet's open group follows the slot being chosen
      const t = tabOf(slot); if (t) S.tab = t.id;
      if (!DESK.matches) S.sheet = {kind: 'pick', slot, opener: opener || null};
      render();
      if (S.sheet) openSheet(); else showSlotInPicker(slot);
    }
    function showSlotInPicker(slot) {
      const list = $('list'), g = list.querySelector('.grp[data-g="' + slot + '"]'); if (!g) return;
      const sh = list.scrollHeight > list.clientHeight;   // the panel scrolls on the computer layout only
      if (sh) list.scrollTo({top: list.scrollTop + g.getBoundingClientRect().top - list.getBoundingClientRect().top - 6, behavior: RM.matches ? 'auto' : 'smooth'});
      g.classList.remove('hot'); void g.offsetWidth; g.classList.add('hot');
      const h = g.querySelector('h3'); if (h) h.focus({preventScroll: true});
    }
    // Remove inside a pick sheet ends that sheet: the part is gone, the rig is shown again
    function removeSlot(sl) {
      S.undo = snapshot(); const nm = items(sl).map(p => p.short).join(', '); delete S.sel[sl];
      const inSheet = S.sheet && S.sheet.kind === 'pick';
      if (inSheet) closeSheet(true);
      toastMsg('Removed ' + esc(nm) + '.', true); announce('Removed ' + nm + '.'); render();
      if (inSheet) focusSlot(sl);
    }
    // "V-mount plate" stays capitalised mid-sentence, "Camera body" becomes "camera body"
    const lc = t => /^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t;
    // returns how many ids were left out because they are no longer on the storefront
    function loadIds(ids) {
      S.sel = {}; let skipped = 0;
      ids.forEach(id => { const p = BY[id]; if (!p) { skipped++; return; } (S.sel[p.slot] = S.sel[p.slot] || []).push(id); });
      S.pin = null;
      return skipped;
    }
    function loadExample() {
      if (!EXAMPLE) return;
      S.undo = snapshot();
      const skipped = loadIds(EXAMPLE.ids); S.tab = 'cage';
      const base = String(EXAMPLE.label || 'example').replace(/\s*example$/i, '');
      toastMsg('Loaded the ' + esc(base) + ' build: ' + allPicked().length + ' parts.' + (skipped ? ' ' + skipped + ' not on the site now, left out.' : ''), true);
      render();
    }
    function clearAll() { S.undo = snapshot(); loadIds([]); S.tab = 'camera'; S.pin = 'camera'; toastMsg('Started an empty rig.', true); render(); }

    /* ---------- numbers ---------- */
    function calc() {
      const ps = allPicked(), total = ps.reduce((s, p) => s + p.price, 0), grams = ps.reduce((s, p) => s + (p.w || 0), 0);
      const cam = one('camera'), bat = one('battery'), mon = one('monitor'), cab = one('cable');
      const power = {wh: bat ? bat.wh : 0, hours: null, load: 0, note: ''};
      // the camera runs off the battery through the dummy cable, or through a plate made to power it (feeds: the PYXIS plates)
      const fed = !!cab || feedsCam();
      if (bat && hasPlate()) {
        let load = 0; if (cam && fed && cam.watt) load += cam.watt; if (mon) load += mon.watt; power.load = Math.round(load * 10) / 10;
        if (cam && fed && !cam.watt) power.note = 'no published draw for the ' + cam.label;
        else if (load > 0) power.hours = bat.wh * 0.85 / load; else power.note = 'add the D-Tap cable or a monitor';
      } else if (bat) power.note = 'battery needs a V-mount plate';
      const dtUsed = (cab ? 1 : 0) + (mon && hasPlate() && bat ? 1 : 0), dtMax = one('plate') ? one('plate').dtaps : 0;
      const extraN = items('rail').length, cap = one('cage') ? 8 + extraN : 0; let used = 0;
      ['onmic', 'wireless', 'arm', 'mmount', 'ff'].forEach(s => { if (one(s)) used++; }); if (hasHandle()) used++; if (one('phone') && !one('arm')) used++; if (hasPlate()) used++;
      return {ps, total, grams, unweighed: ps.filter(p => p.wUnknown).length, power, dtUsed, dtMax, cap, used, lines: ps.length};
    }
    function checks(c) {
      const out = [], cam = one('camera'), cage = one('cage'), ps = c.ps;
      const add = (lv, tag, txt) => out.push({lv, tag, txt});
      if (ps.length && !cam) add('warn', 'No camera', 'Pick a camera body first. Fit checks need to know which body this is.');
      if (cam && !cage && ps.length > 1 && !noCage() && !famPending()) add('warn', 'No cage', 'Handle, arm, mic, plate and focus gear have nothing to mount to.');
      if (cam && FAMNOTE[cam.fam]) add('info', cam.label, FAMNOTE[cam.fam]);
      const xm = items('onmic').find(p => p.needsXlr);
      if (xm && cam) {
        if (cam.xlr === false && hasXlrHandle()) add('ok', 'XLR mic', xm.short + ' plugs into the ' + items('handle').find(p => p.addsXlr).short.replace(/\s*\(XLR\)$/, '') + '.');
        else if (cam.xlr === false) add('warn', 'XLR mic', xm.short + ' is an XLR mic. The ' + cam.label + ' body alone has no XLR inputs.' + (cam.xlrHint ? ' ' + cam.xlrHint : ''));
        else if (cam.xlr === null || cam.xlr === undefined) add('info', 'XLR mic', xm.short + ' is an XLR mic. We have not confirmed the XLR handle option for the ' + cam.label + '. Ask us before ordering.');
        else add('ok', 'XLR mic', xm.short + ' plugs into the XLR handle unit on this camera.');
      }
      const hawkArm = items('arm').find(p => p.hawk);
      if (hawkArm && cage && !cage.hawk) add('warn', 'HawkLock', 'The HawkLock arm needs a HawkLock port. Pick a HawkLock cage for it.');
      if (kit().includes('handle') && one('handle') && !one('handle').addsXlr) add('info', 'Double handle', 'The ' + cage.short + ' already includes a top handle.');
      if (kit().includes('plate') && one('plate')) add('info', 'Double plate', 'The ' + cage.short + ' already includes a V-mount plate.');
      if (one('battery') && !hasPlate()) add('warn', 'Power chain', one('battery').short + ' is a V-mount battery: it needs a V-mount plate.');
      if (one('cable') && !hasPlate()) add('warn', 'Power chain', 'The D-Tap dummy cable needs a D-Tap source: add a V-mount plate and battery.');
      if (hasPlate() && one('battery') && feedsCam()) add('ok', 'Power chain', 'The ' + one('plate').short + ' runs the camera from the V-mount battery.');
      else if (hasPlate() && one('battery') && !one('cable')) add('info', 'Power chain', 'Add the D-Tap dummy cable to run the camera from the V-mount battery.');
      if (one('monitor') && !one('mmount')) add('warn', 'Monitor', 'The monitor has no mount yet.');
      if (one('phone') && !one('arm')) add('warn', 'Phone', 'The phone mount has no arm. Add an articulating arm.');
      const shoes = (one('onmic') ? 1 : 0) + (one('wireless') ? 1 : 0) + (one('phone') && !one('arm') ? 1 : 0);
      if (shoes > 2) add('warn', 'Cold shoes', 'Mic, wireless receiver and phone clamp all need a cold shoe. A cage has two.');
      if (c.cap && c.used > c.cap) add('warn', 'Mounting', 'More things than mounting points. Add a NATO rail.');
      if (cam) add('info', 'Lens', cam.lens ? 'This listing comes with the ' + cam.lens + '.' : 'Lens not included. The ' + cam.label + ' is sold body-only; lenses are separate.');
      if (ps.length && !out.some(o => o.lv === 'warn' || o.lv === 'block')) add('ok', 'No conflicts', 'No compatibility problems found in what is picked.');
      return out.sort((a, b) => ({block: 0, warn: 1, info: 2, ok: 3}[a.lv] - {block: 0, warn: 1, info: 2, ok: 3}[b.lv]));
    }

    /* ---------- readouts, fit check, bar ---------- */
    const tw = {};
    function tween(el, to, fmt, key) {
      const from = tw[key] === undefined ? to : tw[key]; tw[key] = to;
      if (RM.matches || from === to) { el.textContent = fmt(to); return; }
      const t0 = performance.now(), dur = 260; cancelAnimationFrame(el._r);
      const step = now => { const k = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - k, 3); el.textContent = fmt(from + (to - from) * e); if (k < 1) el._r = requestAnimationFrame(step); else el.textContent = fmt(to); };
      el._r = requestAnimationFrame(step);
    }
    function showDelta(d) {
      const el = $('delta'); if (!d) return;
      const t = (d > 0 ? '+ ' : '− ') + money(Math.abs(d));
      el.textContent = t; el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
      const b = $('barSmall'); b.dataset.flash = t; b.classList.add('flash'); clearTimeout(b._t); b._t = setTimeout(() => { b.classList.remove('flash'); delete b.dataset.flash; renderBarSmall(); }, 1700);
    }
    function renderBarSmall() { const b = $('barSmall'); if (b.dataset.flash) b.textContent = b.dataset.flash; else b.innerHTML = S._bar || 'Rig total'; }
    function renderReadouts(c) {
      tween($('rTotal'), c.total, money, 'tot'); tween($('barTotal'), c.total, money, 'bar');
      $('rLines').textContent = c.lines + ' part' + (c.lines === 1 ? '' : 's');
      tween($('rW'), c.grams / 1000, v => '≈ ' + v.toFixed(2) + ' kg', 'w');
      $('rWs').textContent = (c.grams / 1000 * 2.20462).toFixed(1) + ' lb, approx., no lens' + (c.unweighed ? ', ' + c.unweighed + ' part' + (c.unweighed > 1 ? 's' : '') + ' not weighed' : '');
      const pw = c.power;
      if (pw.hours != null) { tween($('rP'), pw.hours, v => '≈ ' + v.toFixed(1) + ' h', 'p'); $('rPs').textContent = 'est. ' + pw.wh + ' Wh at ~' + pw.load + ' W' + (c.dtMax ? ' · D-Tap ' + c.dtUsed + '/' + c.dtMax : ''); }
      else if (pw.wh) { $('rP').textContent = pw.wh + ' Wh'; tw.p = undefined; $('rPs').textContent = pw.note || ''; }
      else { $('rP').textContent = '--'; tw.p = undefined; $('rPs').textContent = 'needs plate, battery, monitor'; }
      $('rM').textContent = c.cap ? c.used + ' / ' + c.cap : '-- / --';
      $('rMs').textContent = c.cap ? 'mounting points, ' + Math.max(0, c.cap - c.used) + ' free' : 'needs a cage';
      let d = ''; for (let i = 0; c.cap && i < Math.max(c.cap, c.used); i++) d += `<b class="${i < c.used ? (i >= c.cap ? 'x' : 'u') : ''}"></b>`; $('rDots').innerHTML = d;
      const ch = checks(c), IC = {ok: '✓', info: 'i', warn: '!', block: '✕'};
      const li = x => `<li class="${x.lv}"><span class="ic" aria-hidden="true">${IC[x.lv]}</span><div><b>${x.tag}<span class="sr"> ${x.lv}</span></b><span>${esc(x.txt)}</span></div></li>`;
      const bad = ch.filter(x => x.lv === 'warn' || x.lv === 'block'), rest = ch.filter(x => x.lv === 'info' || x.lv === 'ok');
      const v = $('verdict');
      v.className = 'verdict ' + (bad.length ? 'warn' : c.lines ? 'ok' : '');
      v.innerHTML = c.lines ? (bad.length ? `<span aria-hidden="true">!</span>${bad.length} to look at` : '<span aria-hidden="true">✓</span>No conflicts') : 'Pick a camera to start';
      $('flags').innerHTML = bad.map(li).join(''); $('flags').style.display = bad.length ? '' : 'none';
      $('notes').innerHTML = rest.map(li).join('');
      $('notesS').textContent = rest.length ? 'Notes (' + rest.length + ')' : 'Notes'; $('notesD').style.display = rest.length ? '' : 'none';
      $('bomN').textContent = '(' + c.lines + ')';
      $('bom').innerHTML = c.ps.slice().sort((a, b) => SLOT[a.slot].n - SLOT[b.slot].n).map(p => `<li><i>${pad(SLOT[p.slot].n)}</i><span><a href="${esc(p.url)}" target="_blank" rel="noopener">${esc(p.name)}</a>${p.variantTitle ? ' · ' + esc(p.variantTitle) : ''}</span><b>${money(p.price)}</b></li>`).join('') || '<li><i>--</i><span>Nothing picked yet.</span><b></b></li>';
      S._bar = c.lines ? c.lines + ' part' + (c.lines === 1 ? '' : 's') + (bad.length ? ' · ' + bad.length + ' to look at' : '') : 'Rig total'; renderBarSmall();
      renderAvail(c);
      $('btnCart').setAttribute('aria-disabled', c.lines ? 'false' : 'true');
      S._bad = bad.length;
      const avail = SLOTS.filter(offered), filled = avail.filter(s => items(s).length || kit().includes(s)).length;
      $('slotsLine').innerHTML = `<b>${filled} of ${avail.length}</b> parts<span class="long"> picked</span>`;
      $('progBar').style.width = (filled / avail.length * 100) + '%';
      const cam = one('camera'); $('camName').textContent = cam ? cam.short.replace(/ body$/, '') : 'No camera yet';
    }

    /* ---------- the summary at the end: what is in store, what ships, what we order ---------- */
    function renderAvail(c) {
      const box = $('avail'); box.hidden = !c.lines; if (!c.lines) return;
      const gs = groupsOf(c.ps);
      $('avSum').textContent = gs.filter(g => g.ps.length).map(g => g.ps.length + ' ' + g.t.toLowerCase()).join(' · ');
      $('avBody').innerHTML = gs.every(g => !g.ps.length || g.k === 'store') ? '<p class="lead">Everything in this rig is on our shelf in Oak Creek.</p>' + summaryHTML(c.ps) : summaryHTML(c.ps);
    }

    /* ---------- quick swap (under the picture) ---------- */
    function optHTML(p) {
      const on = (S.sel[p.slot] || []).includes(p.id);
      return `<button type="button" class="opt" role="${SLOT[p.slot].multi ? 'checkbox' : 'radio'}" aria-checked="${on}" data-id="${p.id}">
       <span class="oi">${hasImg(p) ? imgHTML(p, '', true) : ''}</span><span class="on">${esc(p.short)}</span><span class="op">${money(p.price)}</span></button>`;
    }
    function renderQuick() {
      const q = $('quick'), pin = S.pin;
      if (!pin) {
        const c = calc();
        q.innerHTML = `<div class="qhead plain"><div class="qt"><div class="n">${c.lines ? 'Tap any part to swap it' : 'Pick a camera to begin'}</div><div class="s">${c.lines ? (DESK.matches ? 'Its choices open in the All parts list.' : 'Its choices open in a sheet.') : (EXAMPLE ? 'Or load the ' + esc(EXAMPLE.label.replace(/\s*example$/i, '')) + ' build to see a full rig.' : '')}</div></div></div>`;
        return;
      }
      const it = items(pin), p = it[0], cam = one('camera');
      const fit = p && fitsExact(p) ? ` · <span class="fitok">✓ Fits ${cam.label}</span>` : '';
      const head = p ? `<span class="qth">${hasImg(p) ? imgHTML(p, '') : ''}</span>
         <div class="qt"><div class="k">${pad(SLOT[pin].n)} · ${SLOT[pin].t}</div><div class="n">${esc(slotName(pin))}</div><div class="s">${money(slotPrice(pin))}${fit}</div></div>
         <div class="qacts">${pin !== 'camera' ? `<button class="rbtn" type="button" data-remove="${pin}">Remove</button>` : ''}</div>`
        : `<span class="qth empty" aria-hidden="true">+</span><div class="qt"><div class="k">${pad(SLOT[pin].n)} · ${SLOT[pin].t}</div><div class="n">${SLOT[pin].add}</div><div class="s">${hostMissing(pin) ? 'Needs a ' + ({cage: 'camera', battery: 'V-mount plate', phone: 'arm', monitor: 'monitor mount', cable: 'V-mount plate', card: 'camera'}[pin] || 'cage') + ' first' : 'Choose one below'}</div></div><div></div>`;
      const opts = P.filter(x => x.slot === pin && compat(x));
      // the next step is offered the moment this one is filled: the first empty slot that has something to mount to
      const nx = p ? NEXT_ORDER.find(s => offered(s) && !items(s).length && !kit().includes(s) && !hostMissing(s)) : null;
      const next = nx ? `<div class="qnext"><button class="rbtn alone" type="button" data-slot="${nx}" data-next="1">Next: ${SLOT[nx].add.replace(/^(Add|Pick) /, m => m.toLowerCase())} <span aria-hidden="true">→</span></button></div>` : '';
      q.innerHTML = `<div class="qhead">${head}</div><div class="chooser" role="${SLOT[pin].multi ? 'group' : 'radiogroup'}" aria-label="${SLOT[pin].t} choices">${opts.map(optHTML).join('')}</div>${next}`;
    }

    /* ---------- picker ---------- */
    function renderTabs() {
      $('tabs').innerHTML = TABS.map(t => { const n = t.slots.reduce((s, sl) => s + items(sl).length, 0);
        return `<button class="tab" role="tab" aria-selected="${S.tab === t.id}" data-tab="${t.id}">${t.label}${n ? `<span class="ct" aria-label="${n} picked">${n}</span>` : ''}</button>`; }).join('');
      const cam = one('camera'); $('fitFor').textContent = cam ? 'Fit shown for the ' + cam.label : '';
    }
    function rowHTML(p) {
      const cam = one('camera'), on = (S.sel[p.slot] || []).includes(p.id);
      let fit = '';
      if (p.needsXlr && cam && cam.xlr === false && !hasXlrHandle()) fit = '<span class="fitwarn">▲ Needs XLR handle</span>';
      else if (fitsExact(p)) fit = `<span class="fitok">✓ Fits ${cam.label}</span>`;
      const note = p.note && !fit.includes('fitwarn') ? `<span class="note2">${esc(p.note)}</span>` : '';
      const th = hasImg(p) ? `<span class="th">${imgHTML(p, '', true)}</span>` : '<span class="th ph" aria-hidden="true"></span>';
      return `<button type="button" class="row${S.just === p.id && on && !RM.matches ? ' just' : ''}" role="${SLOT[p.slot].multi ? 'checkbox' : 'radio'}" aria-checked="${on}" data-id="${p.id}">
        ${th}<span><span class="nm">${esc(p.name)}</span><span class="mt"><span>${esc(p.vendor)}</span>${fit}</span>${note}</span>
        <span class="pr">${money(p.price)}${on ? '<small>IN RIG</small>' : ''}</span></button>`;
    }
    // one group of choices for a slot: its heading, its rows, what is left out, a Remove button
    function groupHTML(slot) {
      const cam = one('camera'), all = P.filter(p => p.slot === slot), shown = all.filter(compat), hid = all.length - shown.length;
      let h = `<div class="grp${S.pin === slot ? ' pinned' : ''}" data-g="${slot}"><h3 tabindex="-1"><span>${SLOT[slot].t}</span><span>${slot === 'camera' || slot === 'cage' ? 'Pick one' : SLOT[slot].multi ? 'Pick any' : 'Optional'}</span></h3>`;
      if (hostMissing(slot) && shown.length) h += `<p class="more">Needs a ${({cage: 'camera', battery: 'V-mount plate', phone: 'arm', monitor: 'monitor mount', cable: 'V-mount plate', card: 'camera'}[slot] || 'cage')} first. You can pick it now and add that next.</p>`;
      h += `<div role="${SLOT[slot].multi ? 'group' : 'radiogroup'}" aria-label="${SLOT[slot].t}">${shown.map(rowHTML).join('')}</div>`;
      if (cam && !shown.length && slot !== 'camera' && famPending()) h += `<p class="more">Loading the parts for the ${esc(cam.label)}…</p>`;
      else if (cam && !shown.length && notNeeded(slot)) h += `<p class="more">Not needed: the ${esc(one('plate').short)} runs the camera.</p>`;
      else if (cam && !shown.length && slot !== 'camera') h += `<p class="more">Nothing here is listed for the ${esc(cam.label)} yet. Ask us and we will find one.</p>`;
      else if (hid) h += `<p class="more">${hid} more not listed for the ${cam.label}.</p>`;
      if (items(slot).length && slot !== 'camera') h += `<button class="clear" type="button" data-clear="${slot}">Remove ${lc(SLOT[slot].t)}</button>`;
      return h + '</div>';
    }
    function renderList() {
      const t = TABS.find(x => x.id === S.tab); $('hint').textContent = t.hint;
      let h = t.slots.map(groupHTML).join('');
      if (S.tab === 'camera' && COMING.length) h += `<p class="more">More cameras are on the way: ${esc(COMING.join(', '))}.</p>`;
      const wrap = $('list'), st = wrap.scrollTop; wrap.innerHTML = h; wrap.scrollTop = st;
    }

    /* ---------- the sheet: on a phone the choices for one slot open here; the cart result opens here at any size ---------- */
    function renderSheet() {
      const sh = S.sheet; if (!sh) return;
      if (sh.kind === 'pick') {
        $('sheetT').textContent = 'Choose your ' + lc(SLOT[sh.slot].t);
        const n = P.filter(p => p.slot === sh.slot && compat(p)).length, cam = one('camera');
        $('sheetS').textContent = [sh.slot === 'camera' || sh.slot === 'cage' ? 'Pick one' : SLOT[sh.slot].multi ? 'Pick any' : 'Optional', n + ' choice' + (n === 1 ? '' : 's'),
          cam && sh.slot !== 'camera' ? 'fit shown for the ' + cam.label : ''].filter(Boolean).join(' · ');
        const st = BODY.scrollTop; BODY.innerHTML = groupHTML(sh.slot); BODY.scrollTop = st;
      } else {
        $('sheetT').textContent = sh.title;
        $('sheetS').textContent = '';
        BODY.innerHTML = sh.html;
      }
    }
    function openSheet() {
      const fresh = SHEET.hidden || SHEET.classList.contains('out');
      clearTimeout(closeT); SHEET.classList.remove('out', 'drag'); BACK.classList.remove('out'); SHEET.style.transform = ''; BACK.style.opacity = '';
      if (fresh) BODY.scrollTop = 0;
      renderSheet(); fitLayer();
      BACK.hidden = false; SHEET.hidden = false; layer.classList.add('open'); document.documentElement.classList.add('rigb-open');
      root.inert = true;   // the rig behind is out of reach for taps, Tab and screen readers while the sheet is up
      lockPage();
      if (fresh) {
        // the part already in the rig is in view when the list opens
        const on = BODY.querySelector('.row[aria-checked="true"]');
        if (on) { const br = BODY.getBoundingClientRect(), r = on.getBoundingClientRect(); if (r.bottom > br.bottom - 8) BODY.scrollTop += r.bottom - br.bottom + (br.height - r.height) / 2; }
      }
      SHEET.focus({preventScroll: true});
    }
    function closeSheet(keepRender) {
      const was = S.sheet; if (!was) return;
      S.sheet = null; hideSheet();
      if (!keepRender) { render(); if (was.slot) focusSlot(was.slot); else $('btnCart').focus({preventScroll: true}); }
    }
    // slide away (exit is shorter than the entry), give the page back at once; a sheet opened meanwhile cancels the rest
    function hideSheet() {
      layer.classList.remove('open'); document.documentElement.classList.remove('rigb-open'); root.inert = false; unlockPage(); clearTimeout(closeT);
      const done = () => { SHEET.hidden = true; BACK.hidden = true; SHEET.classList.remove('out', 'drag'); BACK.classList.remove('out'); SHEET.style.transform = ''; BACK.style.opacity = ''; };
      if (RM.matches) { done(); return; }
      SHEET.classList.remove('drag'); SHEET.style.transform = ''; BACK.style.opacity = '';
      SHEET.classList.add('out'); BACK.classList.add('out');
      closeT = setTimeout(done, 240);
    }

    /* the page behind holds still while a sheet is open. Locked on <body> (it carries the viewport's overflow), never on
       <html>: the theme gives body overflow-x:hidden, so hiding html's overflow would make body its own scroller and the
       sticky header and rig bar would jump. A touch on the backdrop or on a list that cannot scroll is stopped too
       (iPhone Safari can still rubber-band a locked page). */
    let lockY = null, lockPrev = null;
    function lockPage() {
      if (lockY !== null) return;
      const b = document.body.style, h = document.documentElement.style;
      lockY = window.scrollY; lockPrev = [b.overflow, h.scrollbarGutter];
      if (innerWidth - document.documentElement.clientWidth > 0) h.scrollbarGutter = 'stable';
      b.overflow = 'hidden';
    }
    function unlockPage() {
      if (lockY === null) return;
      const b = document.body.style, h = document.documentElement.style;
      b.overflow = lockPrev[0]; h.scrollbarGutter = lockPrev[1];
      if (Math.abs(window.scrollY - lockY) > 1) { const sb = h.scrollBehavior; h.scrollBehavior = 'auto'; window.scrollTo(0, lockY); h.scrollBehavior = sb; }
      lockY = null;
    }
    layer.addEventListener('touchmove', e => {
      if (!S.sheet || drag.moved) return;
      const sc = e.target.closest && e.target.closest('.sheetbody');
      if (!sc || sc.scrollHeight <= sc.clientHeight + 1) e.preventDefault();
    }, {passive: false});
    /* swipe down to close: from the grab bar or the title, or from the list when it is scrolled to the top. Let go past a
       third of the sheet (or flick) and it closes; less and it settles back. */
    const drag = {on: false, moved: false};
    SHEET.addEventListener('touchstart', e => {
      drag.on = !!S.sheet && e.touches.length === 1 && (!BODY.contains(e.target) || BODY.scrollTop <= 0);
      drag.moved = false; drag.dy = 0;
      if (drag.on) { drag.y0 = e.touches[0].clientY; drag.t0 = e.timeStamp; }
    }, {passive: true});
    SHEET.addEventListener('touchmove', e => {
      if (!drag.on) return;
      const dy = e.touches[0].clientY - drag.y0;
      if (!drag.moved) {
        if (dy < -3) { drag.on = false; return; }   // upward: the list scrolls as usual
        if (dy < 4) return;                          // a tap, not a drag yet
        drag.moved = true; SHEET.classList.add('drag');
      }
      if (e.cancelable) e.preventDefault();
      drag.dy = Math.max(0, dy);
      SHEET.style.transform = 'translateY(' + drag.dy + 'px)';
      BACK.style.opacity = String(Math.max(0.25, 1 - drag.dy / Math.max(1, SHEET.offsetHeight)));
    }, {passive: false});
    const endDrag = e => {
      if (!drag.on) return; drag.on = false;
      if (!drag.moved) return; drag.moved = false;
      const flick = drag.dy / Math.max(1, e.timeStamp - drag.t0);
      if (drag.dy > Math.min(120, SHEET.offsetHeight / 3) || (flick > 0.5 && drag.dy > 24)) closeSheet();
      else { SHEET.classList.remove('drag'); SHEET.style.transform = ''; BACK.style.opacity = ''; }
    };
    SHEET.addEventListener('touchend', endDrag); SHEET.addEventListener('touchcancel', endDrag);
    // keyboard: Tab stays inside the open sheet
    layer.addEventListener('keydown', e => {
      if (e.key !== 'Tab' || !S.sheet) return;
      const f = [...SHEET.querySelectorAll('button,a[href]')].filter(x => x.offsetParent);
      if (!f.length) return;
      const a = document.activeElement;
      if (e.shiftKey && (a === f[0] || a === SHEET)) { e.preventDefault(); f[f.length - 1].focus(); }
      else if (!e.shiftKey && a === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
    });
    // the button that opened the sheet is redrawn by render(), so find its twin by slot
    // (the visible one: the spec sheet and the tiles both carry every slot, and a closed group's rows are hidden)
    function slotEl(slot) {
      const el = [...root.querySelectorAll('.stage [data-slot="' + slot + '"]')].find(e => e.offsetParent && !e.closest('.qnext'));
      const z = !el && zoneOf(slot); return el || (z ? root.querySelector('.zone[data-z="' + z.id + '"] .zh') : null);
    }
    function focusSlot(slot) { const back = slotEl(slot); if (back) back.focus({preventScroll: true}); }

    /* ---------- misc ---------- */
    let toastT = 0;
    function toastMsg(html, undo, ms) { $('toastT').innerHTML = html; $('toastUndo').hidden = !undo; $('toast').classList.add('on'); clearTimeout(toastT); toastT = setTimeout(hideToast, ms || (undo ? 9000 : 5200)); }
    function hideToast() { $('toast').classList.remove('on'); }
    function announce(s) { $('live').textContent = s; }
    function render() { const c = calc(); renderTabs(); renderList(); renderReadouts(c); Stage.render(); renderQuick(); if (S.sheet && S.sheet.kind === 'pick') renderSheet(); syncUrl(); }
    function ids() { return allPicked().map(p => p.id); }
    function syncUrl() { try { const q = new URLSearchParams(); q.set('r', ids().join(',')); if (S.pin) q.set('pin', S.pin); if (S.view === 'tiles') q.set('v', 'tiles'); history.replaceState(null, '', '#' + q.toString()); } catch (e) {} }

    /* ---------- add to cart: every picked variant in ONE /cart/add.js call ---------- */
    const RIG_ID = 'rig-' + Math.random().toString(36).slice(2, 8);
    const line = p => ({id: p.variantId, quantity: 1, properties: {_rig: RIG_ID}});
    const inOrder = ps => ps.slice().sort((a, b) => SLOT[a.slot].n - SLOT[b.slot].n);
    // Shopify refuses a part that is sold out with Continue selling off (and then refuses the whole call), so those parts
    // are left out of the call: the customer is told which ones we will order for them instead.
    const addable = () => inOrder(allPicked().filter(p => p.avail));
    function cartPayload() { return {items: addable().map(line)}; }
    let adding = false;
    function cartLabel(t) { $('btnCart').innerHTML = '<span>' + t + '</span>'; }
    const post = items => fetch(ROUTES + 'cart/add.js', {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify({items: items.map(line)})});
    // /pages/contact opens with the message already typed (assets: sections/rig-contact-prefill.liquid reads ?message=)
    function askLink(ps) {
      const msg = 'Hi, I am building a rig on your site and would like to ask about ' + (ps.length === 1 ? 'this part' : 'these parts') + ': ' + ps.map(p => p.name).join('; ') + '. Can you tell me when you can have ' + (ps.length === 1 ? 'it' : 'them') + '?';
      return ROUTES + 'pages/contact?message=' + encodeURIComponent(msg);
    }
    const summaryHTML = ps => groupsOf(ps).filter(g => g.ps.length).map(g => `<div class="avg ${g.k}"><h3>${g.t} <span>${g.ps.length}</span></h3><p>${g.s}</p><ul>${g.ps.map(p => `<li>${esc(p.name)}</li>`).join('')}</ul></div>`).join('');
    function showResult(added, left) {
      const n = added.length, all = allPicked();
      let html = `<p class="lead">${n ? `<b>Added ${n} part${n === 1 ? '' : 's'} to your cart.</b>` : '<b>Nothing could be added to your cart yet.</b>'}</p>`;
      if (left.length) html += `<div class="avg order"><h3>We will order ${left.length === 1 ? 'this one' : 'these'} for you <span>${left.length}</span></h3><p>${left.length === 1 ? 'It is' : 'They are'} not in your cart. Ask us and we will get ${left.length === 1 ? 'it' : 'them'} in and tell you when.</p><ul>${left.map(p => `<li>${esc(p.name)}</li>`).join('')}</ul><a class="rbtn alone" href="${esc(askLink(left))}">Ask us about ${left.length === 1 ? 'this' : 'these'}</a></div>`;
      html += `<div class="sheetacts">${n ? `<a class="rbtn main alone" href="${ROUTES}cart">Go to cart</a>` : ''}<button type="button" class="rbtn alone" data-sheetclose="1">Keep building</button></div>`;
      html += '<div class="avs"><h3 class="avh">Where your parts are</h3>' + summaryHTML(all) + '</div>';
      S.sheet = {kind: 'result', title: n ? 'Your rig is in the cart' : 'Your rig', html};
      hideToast(); openSheet();
    }
    async function addRig() {
      const c = calc();
      if (!c.lines) { toastMsg('Pick a camera, or load the example, then add it.'); return; }
      if (adding) return;
      const todo = addable(), left = inOrder(c.ps.filter(p => !p.avail));
      adding = true; $('btnCart').setAttribute('aria-busy', 'true'); cartLabel('Adding…');
      let added = [], failed = false;
      try {
        if (todo.length) {
          const res = await post(todo);
          if (res.ok) added = todo;
          else {
            // one part Shopify refused (sold out meanwhile) refuses the whole call: add the rest one at a time, so it never sinks the rig
            for (const p of todo) { try { if ((await post([p])).ok) added.push(p); else left.push(p); } catch (e) { failed = true; left.push(p); } }
          }
        }
      } catch (e) { failed = true; }
      adding = false;
      if (failed && !added.length) { toastMsg('<b>Nothing was added to your cart.</b> We could not reach the cart. Check your connection and try again.', false, 12000); resetCart(); return; }
      resetCart();
      // everything is in store and in the cart: straight to the cart. Otherwise stop here so the customer reads what is where.
      if (added.length && !left.length && c.ps.every(p => where(p) === 'store')) { toastMsg('Added ' + added.length + ' part' + (added.length === 1 ? '' : 's') + '. Taking you to the cart.'); window.location.href = ROUTES + 'cart'; return; }
      showResult(added, left);
    }
    function resetCart() { adding = false; $('btnCart').removeAttribute('aria-busy'); $('btnCart').innerHTML = '<span>Add<span class="rg"> rig</span> to cart</span>'; }
    window.addEventListener('pageshow', e => { if (e.persisted) resetCart(); });

    const onClick = e => {
      if (e.target.closest('[data-sheetclose]')) { closeSheet(); return; }
      if (S.sheet && S.sheet.closing) return;   // a pick is landing: the sheet is on its way out
      const sl = e.target.closest('[data-slot]');
      if (sl) { openSlot(sl.dataset.slot, sl); return; }
      const tab = e.target.closest('[data-tab]'); if (tab) { S.tab = tab.dataset.tab; renderTabs(); renderList(); return; }
      const row = e.target.closest('.row,.opt'); if (row) { pick(row.dataset.id); return; }
      const cl = e.target.closest('[data-clear]'); if (cl) { removeSlot(cl.dataset.clear); return; }
      const rm = e.target.closest('[data-remove]'); if (rm) { removeSlot(rm.dataset.remove); return; }
    };
    root.addEventListener('click', onClick); layer.addEventListener('click', onClick);
    // iPhone Safari only shows :active (the tap feedback) on a page that listens for touches
    root.addEventListener('touchstart', () => {}, {passive: true});
    $('toastUndo').onclick = () => { if (S.undo) { S.sel = JSON.parse(S.undo); S.undo = null; hideToast(); render(); } };
    $('sheetX').onclick = () => closeSheet();
    $('sheetback').onclick = () => closeSheet();
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && S.sheet) closeSheet(); });
    // a phone turned wide (or a window dragged wide) while a pick sheet is open: the All parts panel takes over
    DESK.addEventListener('change', () => { if (S.sheet && S.sheet.kind === 'pick' && DESK.matches) closeSheet(); });
    if (EXAMPLE) { $('btnExample').textContent = EXAMPLE.label; $('btnExample').hidden = false; $('btnExample').onclick = loadExample; }
    $('btnClear').onclick = clearAll;
    // the rig total in the bar takes a phone back up to the rig, its top clear of the theme's pinned header
    $('btnJump').onclick = () => { if (!DESK.matches) window.scrollBy({top: $('stage').getBoundingClientRect().top - topCover() - 8, behavior: RM.matches ? 'auto' : 'smooth'}); };
    $('btnShare').onclick = async () => {
      const url = location.href.split('#')[0] + '#r=' + ids().join(',') + (S.view === 'tiles' ? '&v=tiles' : '');
      if (!ids().length) { toastMsg('Pick a few parts first, then share the rig.'); return; }
      try { if (navigator.share) { await navigator.share({title: 'My rig from Impulse Cameras', url}); return; } } catch (e) { if (e && e.name === 'AbortError') return; }
      try { await navigator.clipboard.writeText(url); toastMsg('Link copied. Anyone who opens it sees this exact rig.'); }
      catch (e) { toastMsg('Copy this link to share the rig:<br><span style="word-break:break-all;color:var(--quiet)">' + esc(url) + '</span>'); }
    };
    $('btnCart').onclick = addRig;

    /* ---------- A: Spec sheet. One hero photo of the body; parts grouped by where they mount, each group joined to its
       mount point by a thin leader line (computer). On a phone the groups are cards with numbers matching the dots. ---------- */
    const ZONES = [
      {id: 'top', t: 'Top', slots: ['handle', 'onmic', 'wireless', 'rail'], side: 'L', at: [0.50, 0.03]},
      {id: 'arm', t: 'Side', slots: ['arm', 'phone'], side: 'L', at: [0.03, 0.40]},
      {id: 'focus', t: 'Front', slots: ['ff'], side: 'L', at: [0.59, 0.50]},
      {id: 'monitor', t: 'Monitor', slots: ['monitor', 'mmount'], side: 'R', at: [0.88, 0.07]},
      {id: 'body', t: 'Body', slots: ['cage', 'card'], side: 'R', at: [0.985, 0.46]},
      {id: 'power', t: 'Power', slots: ['plate', 'battery', 'cable'], side: 'R', at: [0.50, 0.985]}
    ];
    const zoneOf = slot => ZONES.find(z => z.slots.includes(slot));
    const StageA = {
      zone: null,
      init() {
        root.addEventListener('click', e => {
          const z = e.target.closest('[data-zone]'); if (!z) return;
          const id = z.dataset.zone; this.zone = this.open() === id ? '-' : id; Stage.render();   // one tap closes the open group, even one the pick opened
        });
      },
      open() { if (this.zone === '-') return null; if (this.zone) return this.zone; const z = S.pin && zoneOf(S.pin); return z ? z.id : null; },
      zoneHTML(z, i) {
        const open = this.open() === z.id, cam = one('camera');
        const filled = z.slots.filter(s => items(s).length || kit().includes(s)), nOffered = z.slots.filter(offered).length;
        // nothing in this group is listed for the picked camera: one quiet line, nothing to open
        if (cam && !nOffered && !filled.length) return `<div class="zone none" data-z="${z.id}"><div class="zh"><span class="no">${i + 1}</span><span class="zt">${z.t}&nbsp;&nbsp;<span class="zc">none for the ${esc(cam.label)} yet</span></span><span class="zp"></span></div></div>`;
        const sub = z.slots.reduce((s, sl) => s + slotPrice(sl), 0);
        const on = S.pin && z.slots.includes(S.pin);
        const rows = z.slots.map(sl => {
          const p = one(sl), pr = S.pin === sl;
          if (!p && !offered(sl)) return `<div class="zr none"><span class="ti e" aria-hidden="true">–</span><span class="tx"><span class="k">${SLOT[sl].t}</span><span class="nm2">${notNeeded(sl) ? 'Not needed: the plate runs the camera' : 'None listed for the ' + esc(cam.label) + ' yet'}</span></span></div>`;
          if (!p && kit().includes(sl)) return `<button type="button" class="zr" data-slot="${sl}" aria-pressed="${pr}"><span class="ti e" aria-hidden="true">✓</span><span class="tx"><span class="k">${SLOT[sl].t}</span><span class="nm2">Included in the cage kit</span></span></button>`;
          if (!p) return `<button type="button" class="zr empty" data-slot="${sl}" aria-pressed="${pr}"><span class="ti e" aria-hidden="true">+</span><span class="tx"><span class="k">${SLOT[sl].t}</span><span class="nm2">${SLOT[sl].add}</span></span></button>`;
          const hm = hostMissing(sl);
          return `<button type="button" class="zr${hm ? ' warn' : ''}${S.arrived === sl ? ' arrive' : ''}" data-slot="${sl}" aria-pressed="${pr}"><span class="ti">${hasImg(p) ? imgHTML(p, '') : ''}</span><span class="tx"><span class="k">${SLOT[sl].t}${hm ? ' · ▲ nothing to mount to' : ''}</span><span class="nm2">${esc(slotName(sl))}</span><span class="pz">${money(slotPrice(sl))}</span></span></button>`;
        }).join('');
        const thumbs = z.slots.filter(sl => offered(sl) || one(sl)).map(sl => { const p = one(sl); return hasImg(p) ? `<span>${imgHTML(p, '')}</span>` : '<span class="e"></span>'; }).join('');
        return `<div class="zone${open ? ' open' : ''}${on ? ' on' : ''}" data-z="${z.id}">
          <button type="button" class="zh" data-zone="${z.id}" aria-expanded="${open}"><span class="no">${i + 1}</span><span class="zt">${z.t}&nbsp;&nbsp;<span class="zc">${filled.length}/${nOffered}</span></span><span class="zp">${sub ? money(sub) : ''}</span></button>
          <div class="zthumbs" aria-hidden="true">${thumbs}</div><div class="zrows">${rows}</div></div>`;
      },
      render() {
        const cam = one('camera'), hero = $('hero');
        $('colL').innerHTML = ZONES.map((z, i) => z.side === 'L' ? this.zoneHTML(z, i) : '').join('');
        $('colR').innerHTML = ZONES.map((z, i) => z.side === 'R' ? this.zoneHTML(z, i) : '').join('');
        // phone: two cards per row; if an open group leaves an odd card at the end, it takes the whole row (no dead gap)
        const closed = [...root.querySelectorAll('.zone:not(.open)')];
        if (closed.length % 2) closed[closed.length - 1].classList.add('span');
        if (!cam) {
          hero.innerHTML = '<button type="button" class="none" data-slot="camera"><span class="ring" aria-hidden="true">+</span><b>Pick a camera</b><span>Every part is listed around it.</span></button>';
          $('leads').innerHTML = ''; return;
        }
        const dots = ZONES.map((z, i) => { const any = z.slots.some(s => items(s).length || kit().includes(s)); const on = S.pin && z.slots.includes(S.pin);
          return `<span class="dot${any ? '' : ' empty'}${on ? ' on' : ''}" data-d="${z.id}" style="left:${z.at[0] * 100}%;top:${z.at[1] * 100}%"><i>${i + 1}</i></span>`; }).join('');
        const ar = cam.ar || 1.6;
        hero.innerHTML = `<div class="cam" style="aspect-ratio:${ar}">${hasImg(cam) ? imgHTML(cam, cam.name) : ''}<button type="button" class="camhit${S.pin === 'camera' ? ' on' : ''}${S.arrived === 'camera' ? ' arrive' : ''}" data-slot="camera" aria-label="Camera: ${esc(cam.short)}. Tap to swap."></button>${dots}</div>`;
        const img = hero.querySelector('img');
        if (!img) this.lines(); else if (img.complete) this.lines(); else img.onload = () => this.lines();
        requestAnimationFrame(() => this.lines());
      },
      lines() {
        const svg = $('leads'); if (!DESK.matches || !one('camera')) { svg.innerHTML = ''; return; }
        const box = $('spec').getBoundingClientRect();
        let h = '';
        ZONES.forEach(z => {
          const el = root.querySelector(`.zone[data-z="${z.id}"] .zh`), d = root.querySelector(`.dot[data-d="${z.id}"]`); if (!el || !d) return;
          const r = el.closest('.zone').getBoundingClientRect(), q = el.getBoundingClientRect(), dr = d.getBoundingClientRect();
          const x0 = (z.side === 'L' ? r.right : r.left) - box.left, y0 = q.top + q.height / 2 - box.top;
          const x2 = dr.left - box.left, y2 = dr.top - box.top, x1 = x0 + (z.side === 'L' ? 16 : -16);
          const any = z.slots.some(s => items(s).length || kit().includes(s)), on = S.pin && z.slots.includes(S.pin);
          h += `<path class="${any ? '' : 'empty'}${on ? ' on' : ''}" d="M${x0},${y0} H${x1} L${x2},${y2}"/>`;
        });
        svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`); svg.innerHTML = h;
      },
      resize() { this.lines(); }
    };

    /* ---------- C: Tiles (the flight case). Every slot is a fixed pocket in a fitted case; the camera and cage take the
       big pockets. A part drops into its pocket; an empty pocket says what goes there. ---------- */
    const SHAPE = {camera: 'big', cage: 'big', monitor: 'wide', onmic: 'wide', handle: 'wide'};
    const StageC = {
      render() {
        const pin = S.pin; let h = '';
        SLOTS.forEach(sl => {
          const it = items(sl), p = it[0], shape = SHAPE[sl] || 'small', on = pin === sl ? ' on' : '';
          let inner = '', cls = 'pk ' + shape + (shape === 'wide' ? ' w2' : '') + on;
          if (p) {
            const hm = hostMissing(sl);
            if (hm) cls += ' hm';
            if (S.arrived === sl && !RM.matches) cls += ' settle';
            inner = hasImg(p) ? imgHTML(p, '') : '<span class="ph">No studio photo yet</span>';
            if (shape === 'big') inner += `<span class="cap"><b>${esc(slotName(sl))}</b><i>${money(slotPrice(sl))}</i></span>`;
            else inner += `<span class="tagn">${SLOT[sl].s}<span class="tp">${money(slotPrice(sl))}</span></span>`;
            if (it.length > 1) inner += `<span class="bd">×${it.length}</span>`;
            if (hm) inner += '<span class="bd warn" aria-hidden="true">!</span>';
          } else if (!offered(sl)) {
            h += `<div class="pk ${shape}${shape === 'wide' ? ' w2' : ''} none" style="grid-area:${sl}"><span class="nil">${SLOT[sl].s}<br>${notNeeded(sl) ? 'not needed' : 'none for ' + esc(one('camera').label)}</span></div>`;
            return;
          } else if (kit().includes(sl)) {
            inner = `<span class="add kit"><b>✓</b>${SLOT[sl].s}<br>in cage kit</span>`;
          } else {
            inner = `<span class="add"><b aria-hidden="true">+</b>${SLOT[sl].s}</span>`;
          }
          const lab = p ? `${SLOT[sl].t}: ${slotName(sl)}, ${money(slotPrice(sl))}. Tap to swap.` : `${SLOT[sl].t}: empty. Tap to choose.`;
          h += `<button type="button" class="${cls}" style="grid-area:${sl}" data-slot="${sl}" aria-label="${esc(lab)}" aria-pressed="${pin === sl}">${inner}</button>`;
        });
        $('case').innerHTML = h;
      }
    };

    /* ---------- the view switch: one rig, two views. Both draw from the same S.sel, so the picks, the total, the fit check
       and Add to cart can never differ between them. The view is kept in the address (#...&v=tiles) and sessionStorage. ---------- */
    const Stage = {
      init() {
        StageA.init();
        root.addEventListener('click', e => { const b = e.target.closest('[data-show]'); if (b) setView(b.dataset.show); });
      },
      render() {
        const v = S.view; root.dataset.view = v;
        root.querySelectorAll('.vsw [data-show]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.show === v)));
        if (v === 'tiles') StageC.render(); else StageA.render();
      },
      resize() { if (S.view === 'spec') StageA.resize(); }
    };
    function setView(v) {
      if (v === S.view) return;
      S.view = v; try { sessionStorage.setItem('rigView', v); } catch (e) {}
      render();
      const el = v === 'tiles' ? root.querySelector('.caseout') : $('spec');
      el.classList.remove('viewin'); void el.offsetWidth; el.classList.add('viewin');
      announce(v === 'tiles' ? 'Showing tiles.' : 'Showing the spec sheet.');
    }

    /* ---------- boot ---------- */
    function boot() {
      const h = new URLSearchParams(location.hash.slice(1));
      let skipped = 0;
      if (h.has('r')) skipped = loadIds((h.get('r') || '').split(',').filter(Boolean)); else loadIds([]);
      if (h.get('pin') && SLOT[h.get('pin')]) { S.pin = h.get('pin'); S.tab = tabOf(S.pin).id; }
      else S.tab = allPicked().length ? 'cage' : 'camera';
      let v = h.get('v'); if (!v) { try { v = sessionStorage.getItem('rigView'); } catch (e) {} }
      S.view = v === 'tiles' ? 'tiles' : 'spec';
      Stage.init();
      render();
      if (skipped) toastMsg(skipped + ' part' + (skipped === 1 ? '' : 's') + ' in this shared rig ' + (skipped === 1 ? 'is' : 'are') + ' not available now and ' + (skipped === 1 ? 'was' : 'were') + ' left out.', false, 9000);
      tw.tot = undefined;
      new ResizeObserver(() => { Stage.resize(); }).observe($('stage'));
    }
    // a small, read-only handle for tests and for the day this becomes a bundle: what the cart call would send, and the on-page total
    window.ImpulseRig = {cartPayload, total: () => calc().total, parts: () => allPicked().map(p => ({id: p.id, handle: p.handle, variantId: p.variantId, price: p.price})),
      groups: () => Object.fromEntries(groupsOf(allPicked()).map(g => [g.k, g.ps.map(p => p.id)])),
      // what the page worked out for one part from the live data, so a test can check it against the raw view
      facts: id => { const p = BY[id]; return p && {where: where(p), avail: p.avail, qty: p.stock, inStore: p.inStore, supplier: p.supplier}; },
      // the config handles the storefront did not answer for (left out of the page), and what the fit check says now
      missing: () => missing.slice(), ready: () => !pending, checks: () => checks(calc()).map(x => ({lv: x.lv, tag: x.tag, txt: x.txt}))};
    boot();
  }
})();
